import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Button,
    CircularProgress,
    Grid,
    FormControl,
    TextField,
    Typography
} from "@mui/material/";
import actions from "../../../../Store/actions";
import { strings } from "../../../../common/i18n";
import makeStyles from "@mui/styles/makeStyles";
import styles from "../styles";
import { logToSentry } from "../../../../config/Sentry";

type Props = {
    onClose: Function,
    imageGeoLocation?: {
        latitude: number;
        longitude: number;
        location: number;
        id: number;
    }
};
/**
 * Update Geo location Component
 */

/** Initial geo location state */
const initialGeoLocation = {
    latitude: 0.1,
    longitude: 0.1,
    location: 0,
    id: 0
};
export default ({ onClose, imageGeoLocation: propImageGeoLocation }: Props) => {
    const [geoLocation, setGeoLocation] = useState(initialGeoLocation);
    const [imageGeoLocation, setImageGeoLocation] = useState(propImageGeoLocation || initialGeoLocation); //Sets intial imageGeoLocation if not uses initialGeoLocation
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const { locations } = useSelector((state) => state);
    const dispatch = useDispatch();
    const { item } = locations;

    const classes = makeStyles(styles)();

    useEffect(() => {
        if (item) {
            // Set dealer coordinates
            if (item.geo && item.geo.length > 0) {
                setGeoLocation(item.geo[0]);
            }
            // Set image coordinates
            const imageData = item?.audit_asset_content?.[0]?.audit_images?.[0];
            if (imageData) {
                setImageGeoLocation({
                    latitude: imageData?.latitude ?? 0,
                    longitude: imageData?.longitude ?? 0,
                    location: 0,
                    id: 0
                });
                
            }
        }
    }, [item]);
    
    const onImageGeoChange = (evt: Event) => {
        const { name, value } = evt.target;
        setImageGeoLocation((prev) => {
            const updated = { ...prev, [name]: value };
            onValidateLatLng(updated);
            return updated;
        });
    };

    const onGeoChange = (evt: Event) => {
        try {
            const { name, value } = evt.target;
            setGeoLocation((geoLocation) => {
                const modifiedValue = { ...geoLocation, [name]: value };
                onValidateLatLng(modifiedValue);
                return modifiedValue;
            });
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On geo change:", { error });
            dispatch(actions.toast.create.error(strings("errors.onGeoChange")));
        }
    }; 

    const onValidateLatLng = (geoLocation) => {
        const { latitude, longitude } = geoLocation;
        if (latitude.length === 0 || longitude.length === 0) {
            setError(strings("messages.geoEmpty"));
        } else if (!(isFinite(latitude) && Math.abs(latitude) <= 90)) {
            setError(strings("messages.latitudeErrorMessage"));
        } else if (!(isFinite(longitude) && Math.abs(longitude) <= 180)) {
            setError(strings("messages.longitudeErrorMessage"));
        } else setError("");
    };

    const onSave = async () => {
        try {
            setLoading(true);
            const updateGeo = {
                id: geoLocation.id,
                latitude: parseFloat(parseFloat(imageGeoLocation.latitude).toFixed(6)),
                longitude: parseFloat(parseFloat(imageGeoLocation.longitude).toFixed(6)),
            };
            await dispatch(actions.locations.create.patchGeoRequest(updateGeo));    
            
            dispatch(
                actions.toast.create.success(strings("updatedSuccessfully"))
            );
            onClose && onClose();
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On geo change:", { error });
            dispatch(actions.toast.create.error(strings("errors.onGeoChange")));
        }
        setLoading(false);
    };

    return (
        <form>
            <FormControl fullWidth margin="normal">
                <Typography variant="body1">
                    {strings("messages.updateLocation")}
                </Typography>
                <FormControl margin="normal">
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label={strings("dealerlatitude")}
                                onChange={onGeoChange}
                                name="latitude"
                                value={geoLocation.latitude}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label={strings("imagelatitude")}
                                onChange={onImageGeoChange}
                                name="latitude"
                                value={imageGeoLocation.latitude}
                            />
                        </Grid>
                    </Grid>
                </FormControl>
                <FormControl fullWidth margin="normal">
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label={strings("dealerlongitude")}
                                name="longitude"
                                onChange={onGeoChange}
                                value={geoLocation.longitude}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label={strings("imagelongitude")}
                                name="longitude"
                                onChange={onImageGeoChange}
                                value={imageGeoLocation.longitude}
                            />
                        </Grid>
                    </Grid>
                </FormControl>
            </FormControl>
            {error.length > 0 ? (
                <Typography component="div" className={classes.error}>
                    {error}
                </Typography>
            ) : null}
            <FormControl fullWidth margin="normal">
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            size="medium"
                            variant="outlined"
                            onClick={onClose}
                        >
                            {strings("cancel")}
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            color="primary"
                            size="medium"
                            variant="outlined"
                            disabled={loading || error.length > 0}
                            onClick={onSave}
                        >
                            {loading ? (
                                <CircularProgress size={24} />
                            ) : (
                                strings("update")
                            )}
                        </Button>
                    </Grid>
                </Grid>
            </FormControl>
        </form>
    );
};
